import type { FeatureFlag } from '@forgd/shared/feature-flags'
import type { IconClass } from '#core/types'
import { useAdvisor } from '#advisor-portal/state/useAdvisor'
import { closeSlideoverOnMouseover, handleSlideoverChange } from '@forgd/ui-kit/blocks/nav'
import { BanknotesIcon, CubeIcon, GlobeAltIcon, LightBulbIcon, PuzzlePieceIcon } from '@heroicons/vue/24/outline'
import { withTrailingSlash } from 'ufo'

export function useNavExpanded() {
  return useState<boolean | 'responsive'>('nav:expanded', () => false)
}

interface NavLink {
  icon?: IconClass | `/${string}`
  iconComponent?: Component
  to?: string
  isProjectScoped?: boolean
  label?: string
  key?: string
  external?: boolean
  testId?: string
  children?: NavLink[]
  isStrict?: boolean
  on?: Record<string, (event: MouseEvent) => void>
  shouldHighlight?: (currentPath: string) => boolean
  coreTool?: boolean
  featureFlag?: FeatureFlag
}

export function filterNavLinksByFeatureFlags(navLinks: NavLink[]): NavLink[] {
  const { featureFlags } = useAuth()
  const filterItems = (items: NavLink[]): NavLink[] =>
    items
      .filter(item => !item.featureFlag || featureFlags.includes(item.featureFlag))
      .map(item => (item.children ? { ...item, children: filterItems(item.children) } : item))
  return filterItems(navLinks)
}

const aboutForgdLink: NavLink = {
  to: 'https://forgd.com/about',
  icon: 'i-heroicons-information-circle',
  label: 'About Forgd',
  external: true,
}

const supportLink: NavLink = {
  to: '/support',
  icon: 'i-heroicons-chat-bubble-oval-left-ellipsis',
  label: 'Support',
  isStrict: true,
}

const accountSettingsLink: NavLink = {
  icon: 'i-heroicons-cog-8-tooth',
  to: '/settings',
  label: 'Account Settings',
  isStrict: true,
  isProjectScoped: true,
}

const logoutLink: NavLink = {
  icon: 'i-heroicons-arrow-right-start-on-rectangle',
  label: 'Logout',
  testId: 'logout',
}

const marketMakerLeaderboardNavItem: NavLink = {
  key: 'market-maker-leaderboard',
  label: 'Market Maker Leaderboard',
  to: '/market-maker-leaderboard',
  icon: 'i-heroicons-trophy',
  shouldHighlight: (path: string) => path.startsWith('/market-maker-leaderboard'),
}

const universalTokenRatingNavItem: NavLink = {
  key: 'universal-token-rating',
  label: 'Universal Token Rating',
  to: '/universal-token-rating',
  icon: 'i-heroicons-viewfinder-circle',
  shouldHighlight: (path: string) => path.startsWith('/universal-token-rating'),
}

const universalExchangeRatingNavItem: NavLink = {
  key: 'universal-exchange-rating',
  label: 'Universal Exchange Rating',
  to: '/universal-exchange-rating',
  icon: 'i-heroicons-scale',
  featureFlag: 'universal-exchange-rating',
  shouldHighlight: (path: string) => path.startsWith('/universal-exchange-rating'),
}

function getPublicResearchMenu() {
  const auth = useAuth()
  const advisor = useAdvisor()
  const expanded = useNavExpanded()
  const { destination } = useConsultationDestination()

  const menu = computed<NavLink[][]>(() => {
    const topMenu = [
      [
        {
          key: 'public-research-tokenomics',
          label: 'Tokenomics Research',
          to: '/research/tokenomics',
          icon: 'i-heroicons-chart-pie' as IconClass,
          isStrict: true,
        },
        {
          key: 'public-research-exchange-listing',
          label: 'Exchange & Listing Research',
          to: '/research/exchange-and-listing',
          icon: 'i-heroicons-building-library' as IconClass,
          isStrict: true,
        },
        {
          ...marketMakerLeaderboardNavItem,
          key: 'public-research-market-maker-leaderboard',
        },
        {
          ...universalTokenRatingNavItem,
          key: 'public-research-universal-token-rating',
        },
        {
          ...universalExchangeRatingNavItem,
          key: 'public-research-universal-exchange-rating',
        },
        {
          key: 'public-research-academy-overview',
          label: 'Forgd Academy',
          icon: 'i-heroicons-academic-cap',
          to: '/academy',
          on: closeSlideoverOnMouseover,
          shouldHighlight: (currentPath: string) => {
            return currentPath.includes('/academy')
          },
        },
      ],
      auth.isLoggedIn
        ? [
            {
              key: 'public-research-project-dashboard',
              label: auth.isAdvisor ? 'Advisor Dashboard' : 'Project Home',
              to: '/',
              icon: 'i-heroicons-home' as IconClass,
            },
          ]
        : undefined,
      !expanded.value
        ? [
            {
              key: 'public-research-schedule-consultation',
              label: 'Book a Free Consultation',
              to: destination.value.to,
              icon: 'i-heroicons-calendar-days' as IconClass,
              external: destination.value.external,
            },
          ]
        : undefined,
    ].filter(Boolean) as NavLink[][]

    const bottomMenu = [
      aboutForgdLink,
      supportLink,
      auth.isLoggedIn ? accountSettingsLink : undefined,
      auth.isLoggedIn
        ? {
            ...logoutLink,
            on: {
              click: () => {
                auth.logout(undefined, false)
                advisor.reset()
              },
            },
          }
        : undefined,
    ].filter(Boolean) as NavLink[]

    return [...topMenu.map(filterNavLinksByFeatureFlags), bottomMenu]
  })

  return { menu }
}

export async function useTopLevelMenu(variant: 'public-research' | 'dashboard' = 'dashboard') {
  if (variant === 'public-research') {
    return getPublicResearchMenu()
  }

  const auth = useAuth()
  const advisor = useAdvisor()
  const { project } = storeToRefs(advisor)
  const { destination } = useConsultationDestination()

  const menu = computed<NavLink[][]>(() => {
    const advisorDashboard: NavLink = {
      icon: 'i-heroicons-squares-2x2',
      to: '/advisor-portal',
      label: 'Dashboard & Playbook',
      on: closeSlideoverOnMouseover,
      isStrict: true,
    }

    const projectDashboard: NavLink = {
      icon: 'i-heroicons-home',
      to: '/',
      label: 'Home',
      on: closeSlideoverOnMouseover,
      isStrict: true,
      isProjectScoped: true,
      shouldHighlight: (currentPath: string) => {
        return currentPath === '/' || /^\/project\/[^/]+\/?$/.test(currentPath)
      },
    }

    const topMenu: NavLink[] = auth.isAdvisor && (!advisor.isInProject || advisor.isInPortal)
      ? [
          advisorDashboard,
          {
            icon: 'i-heroicons-users',
            to: '/advisor-portal/teammates',
            label: 'Manage Teammates',
            on: closeSlideoverOnMouseover,
            isStrict: true,
          },
        ]
      : [
          auth.isAdvisor && advisor.isInPortal ? advisorDashboard : projectDashboard,
          {
            icon: 'i-forgd-tools',
            label: 'Tools & Services',
            key: 'tools-and-services',
            on: {
              mouseenter(event: MouseEvent) {
                handleSlideoverChange({
                  event,
                  slideover: 'tools-and-services',
                })
              },
            },
          },
          {
            icon: 'i-heroicons-cog-8-tooth',
            to: '/token-settings',
            label: 'Token Settings',
            on: closeSlideoverOnMouseover,
            isProjectScoped: true,
          },
        ]

    const secondaryMenu: NavLink[] = [
      {
        ...marketMakerLeaderboardNavItem,
        label: 'Market Maker Leaderboards',
        on: closeSlideoverOnMouseover,
      },
      {
        ...universalTokenRatingNavItem,
        on: closeSlideoverOnMouseover,
      },
      {
        ...universalExchangeRatingNavItem,
        on: closeSlideoverOnMouseover,
      },
      {
        icon: 'i-heroicons-academic-cap',
        label: 'Forgd Academy',
        to: '/academy',
        on: closeSlideoverOnMouseover,
        shouldHighlight: (currentPath: string) => {
          return currentPath.includes('/academy')
        },
      },
      ...(!(auth.isAdvisor && (!advisor.isInProject || advisor.isInPortal))
        ? [{
            label: 'Schedule a Consultation',
            to: destination.value.to,
            external: destination.value.external,
            icon: 'i-heroicons-calendar-days' as IconClass,
            on: closeSlideoverOnMouseover,
          }]
        : []),
    ]

    const menu = [
      filterNavLinksByFeatureFlags(topMenu),
      filterNavLinksByFeatureFlags(secondaryMenu),
    ]

    if (auth.isAdvisor && advisor.isInProject && !advisor.isInPortal) {
      menu.push([{
        icon: 'i-heroicons-arrow-right-start-on-rectangle',
        label: `Exit ${project.value?.name}`,
        testId: 'exit-project',
        on: {
          click: advisor.exitProject,
          ...closeSlideoverOnMouseover,
        },
      }])
    }

    const bottomMenu: NavLink[] = [
      {
        ...accountSettingsLink,
        on: closeSlideoverOnMouseover,
      },
      {
        ...aboutForgdLink,
        on: closeSlideoverOnMouseover,
      },
      {
        ...supportLink,
        on: closeSlideoverOnMouseover,
      },
      {
        ...logoutLink,
        on: {
          click: () => {
            logout()
            advisor.reset()
          },
          ...closeSlideoverOnMouseover,
        },
      },
    ]

    return [
      ...menu,
      bottomMenu,
    ] satisfies NavLink[][]
  })

  return { menu }
}

export function isCurrent({ to, shouldHighlight }: NavLink, strict: boolean = false) {
  const route = useRoute()
  if (to === undefined) {
    return false
  }
  if (shouldHighlight) {
    return shouldHighlight(route.path)
  }
  // we just care about the first path segment matching
  const pathSegments = (to || '').split('/').filter(Boolean)
  const currPathSegments = (route.path || '').split('/').filter(Boolean)
  if (!strict) {
    if (withTrailingSlash(currPathSegments[0]) === withTrailingSlash(pathSegments[0])) {
      return true
    }
  }
  // special logic, we match the top-level category which is the first 3 segments
  if (route.path.startsWith('/academy')) {
    const categoryPath = currPathSegments.slice(0, 3).join('/')
    return withTrailingSlash(categoryPath) === withTrailingSlash(pathSegments.slice(0, 3).join('/'))
  }
  // requires full match
  return withTrailingSlash(to) === withTrailingSlash(route.path)
}

const DesignTokenomics: NavLink = {
  label: 'Design Tokenomics',
  to: '/design-tokenomics',
  icon: '/icons/design-tokenomics.svg',
  coreTool: true,
}
const TokenUnlockMonitoring: NavLink = {
  label: 'Token Unlock Monitoring',
  to: '/token-unlocks',
  icon: '/icons/token-unlock-monitoring.svg',
  coreTool: true,
  isProjectScoped: true,
}
const EngageAMarketMaker: NavLink = {
  label: 'Engage a Market Maker',
  to: '/engage-a-market-maker',
  icon: '/icons/engage-a-market-maker.svg',
  coreTool: true,
  isProjectScoped: true,
}
const ExchangeListingResearch: NavLink = {
  label: 'Exchange Listing Research',
  to: '/list-on-exchanges',
  icon: '/icons/exchange-listing-research.svg',
  coreTool: true,
  isProjectScoped: true,
}
const MarketMakerMonitoring: NavLink = {
  label: 'Market Maker Monitoring',
  to: '/market-maker-monitoring',
  icon: '/icons/market-maker-monitoring-next.svg',
  coreTool: true,
}
const MarketMakerLeaderboards: NavLink = {
  label: 'Market Maker Leaderboards',
  to: '/market-maker-leaderboard',
  icon: '/icons/market-maker-leaderboards.svg',
  coreTool: true,
}
export const coreTools: NavLink[] = [
  DesignTokenomics,
  TokenUnlockMonitoring,
  EngageAMarketMaker,
  MarketMakerMonitoring,
  MarketMakerLeaderboards,
  ExchangeListingResearch,
]

export const preTgeNav: NavLink[] = [
  {
    label: 'Tokenomics & Protocol',
    icon: 'i-heroicons-cube',
    iconComponent: CubeIcon,
    children: [
      DesignTokenomics,
      { label: 'Token Unlocks', to: '/token-unlocks', isProjectScoped: true },
    ],
  },
  {
    label: 'Finance and Fundraising',
    icon: 'i-heroicons-banknotes',
    iconComponent: BanknotesIcon,
    children: [
      { label: 'Cashflow Analysis', to: 'https://docs.google.com/spreadsheets/d/13TsO0y64pvv1mAbParQjK7AIIK0FxICc3CtBIQd3NTk/edit?gid=798759478#gid=798759478' },
      { label: 'Growth Capital', to: 'https://docs.google.com/spreadsheets/d/1FWjsPxQALgSIOeXEmuvbRqb-PiWhZtWCvjGyQXegTFY/edit?gid=147303761#gid=147303761' },
      { label: 'Go-to-Market Budgeting', to: 'https://docs.google.com/spreadsheets/d/1P0RhK4QYrTt9cqfCpf46qV1FnpH82N7CtlYkei3ISgU/edit?gid=1931257942#gid=1931257942' },
    ],
  },
  {
    label: 'Go-to-Market Partnership',
    icon: 'i-heroicons-globe-alt',
    iconComponent: GlobeAltIcon,
    children: [
      { label: 'Listing & Liquidity Academy', to: 'https://docsend.com/view/fexpeu4nxmvt8j72' },
      EngageAMarketMaker,
      ExchangeListingResearch,
      { label: 'Marketing & KOL Support', to: 'https://docs.google.com/spreadsheets/d/1k7pyJe5x24wBnMET428rNqMTaKvPlsMqE1lZlLqeoC0/edit?gid=1326628848#gid=1326628848' },
      { label: 'Launch Checklist', to: 'https://docs.google.com/spreadsheets/d/1zo0zxfqXzvivroHi38jO0wHXLvpmRPO1dc6BRMMsB14/edit?usp=sharing' },
    ],
  },
]

export const listedTokenNav: NavLink[] = [
  {
    label: 'Analysis & Insights',
    icon: 'i-heroicons-light-bulb',
    iconComponent: LightBulbIcon,
    children: [
      { label: 'Market Maker Monitoring', to: '/market-maker-monitoring' },
      TokenUnlockMonitoring,
    ],
  },
  {
    label: 'Strategic Partnership',
    icon: 'i-heroicons-puzzle-piece',
    iconComponent: PuzzlePieceIcon,
    children: [
      { label: 'Listing & Liquidity Academy', to: 'https://docsend.com/view/fexpeu4nxmvt8j72' },
      EngageAMarketMaker,
      ExchangeListingResearch,
      { label: 'Marketing & KOL Support', to: 'https://docs.google.com/spreadsheets/d/1k7pyJe5x24wBnMET428rNqMTaKvPlsMqE1lZlLqeoC0/edit?gid=1326628848#gid=1326628848' },
    ],
  },
  {
    label: 'Finance and Fundraising',
    icon: 'i-heroicons-banknotes',
    iconComponent: BanknotesIcon,
    children: [
      { label: 'Cashflow Analysis', to: 'https://docs.google.com/spreadsheets/d/1xElL1SBH88T1xyxTCpne2qPfJy7vouoDp2UZOzsO18A/edit?gid=798759478#gid=798759478' },
      { label: 'Growth Capital', to: 'https://docs.google.com/spreadsheets/d/1FWjsPxQALgSIOeXEmuvbRqb-PiWhZtWCvjGyQXegTFY/edit?gid=147303761#gid=147303761' },
    ],
  },
]
